import { Popover } from "@mui/material";
import * as React from "react";
import { Preferences } from "../../hooks/useUserPreferences";
import { DraggableAircraft } from "../../screens/Hangar/HangarLayout/DraggableAircraftNew";
import { calculateObstacleImageDimensions } from "../../screens/Hangar/HangarLayout/DraggableAircraftNew/Scale";
import { getObstacleImageUrl } from "../../screens/SuperAdmin/MovableObstacleAdmin/utils/getObstacleImageUrl";
import { MovableObstacle, Position, Stack } from "../../types";
import { MovableObstacleContextMenu } from "../CurrentAircraft/AircraftPreferencesListItemMenu";
import { RightClick } from "./Layout";

type Props = {
  layoutHeight: number;
  feetToPixels: number;
  movableObstacle: MovableObstacle;
  stack: Stack;
  setStack: (stack: Stack) => void;
  preferences: Preferences;
  onPositionChange: (placement_id: string, position: Position) => void;
  onChange?: (placement_id: string, position: Position) => void;
  rightClick: RightClick;
  setRightClick: (rightClick: RightClick) => void;
  spacingBufferOverlayElement?: HTMLDivElement | null;
  readOnly?: boolean;
};

export const FullyLoadedDraggableObstacle: React.FC<Props> = ({
  movableObstacle,
  feetToPixels,
  stack,
  setStack,
  preferences,
  layoutHeight,
  onPositionChange,
  rightClick,
  setRightClick,
  spacingBufferOverlayElement,
  readOnly = false,
}) => {
  const imageDimensions = {
    ...calculateObstacleImageDimensions(feetToPixels, movableObstacle),
  };

  const position = {
    x: movableObstacle.position.x * feetToPixels,
    y: movableObstacle.position.y * feetToPixels,
    angle: movableObstacle.position.angle,
    timestamp: movableObstacle.position.timestamp,
  };

  // This assumes the y position is an alignment to the bottom of the
  // hangar and this reverses it to align to the top to pass on to
  // the DraggableAircraftNew
  position.y = layoutHeight - (position.y + imageDimensions.height);
  return (
    <>
      <DraggableAircraft
        entity_id={movableObstacle.entity_id}
        hideDragIndicator
        readOnly={readOnly}
        spacingBufferOverlayElement={
          !readOnly && preferences.showSOPSpacing
            ? spacingBufferOverlayElement
            : null
        }
        forceDisplayLabel={
          preferences.forceDisplayTailNumber || preferences.forceDisplayModel
        }
        label={movableObstacle.obstacle.name}
        aircraft={{
          // From PredefinedObstacle
          id: movableObstacle.obstacle.id,
          length: movableObstacle.obstacle.length,
          geom: movableObstacle.obstacle.geom ?? null,
          geomz: movableObstacle.obstacle.geomz ?? null,
          image: getObstacleImageUrl(movableObstacle.obstacle.id),
          created_at: movableObstacle.obstacle.created_at ?? new Date().toISOString(),

          // Required Aircraft fields with sensible defaults
          linked_to: "",
          make: "",
          model: movableObstacle.obstacle.name,
          wingspan: movableObstacle.obstacle.width,
          midline: movableObstacle.obstacle.length / 2,
          tail_height: movableObstacle.obstacle.height,
          side_image: "",
          nose: null,
          centroid: null,
          wheel_rotation_limit: 0,
          front_wheels: 0,
          rear_wheels: 300,
          wing_geom: null,
          wing_top: 0,
          wing_bottom: 0,
          tail_geom: null,
          tail_top: 0,
          tail_bottom: 0,
          vertical_tail_fin_geom: null,
          vertical_tail_fin_top: 0,
          vertical_tail_fin_bottom: 0,
          cabin_top: 0,
          cabin_bottom: 0,
          reference_point_wing_bottom: 0,
          reference_point_wing_top: 0,
          reference_point_tail_bottom: 0,
          reference_point_tail_top: 0,
          reference_point_cabin_bottom: 0,
          reference_point_cabin_top: 0,
          reference_point_vertical_tail_fin_bottom: 0,
          reference_point_vertical_tail_fin_top: 0,
          propeller_geom: null,
          placement_id: movableObstacle.placement_id,
          tail_number: movableObstacle.obstacle.name,

          // Position and state
          position: position,
          selected: movableObstacle.selected,
          spacing_buffer_geom: null,
          spacing_buffer_geom_dimensions: { width: 0, height: 0 },
        }}
        imageDimensions={imageDimensions}
        onPositionChange={(position) => {
          // Adjust the position to be aligned to the bottom
          // as it will come from the component aligned to the top

          // Switch the y position to align to the bottom of the hangar
          position.y = layoutHeight - (position.y + imageDimensions.height);
          const fullPosition: Position = {
            id: movableObstacle.position.id,
            stack_id: movableObstacle.position.stack_id,
            entity_id: movableObstacle.entity_id,
            x: position.x / feetToPixels,
            y: position.y / feetToPixels,
            angle: position.angle,
            preferences: movableObstacle.position.preferences ?? [],
            timestamp: position.timestamp,
          };
          onPositionChange(movableObstacle.entity_id, fullPosition);
        }}
        onChange={() => {
          //
        }}
        onPlaneClick={() => {
          // moved to the global onclick event
        }}
        onPlaneRightClick={() => {
          // moved to the global onclick event
        }}
        onStartTowing={() => {
          return;
          // we want to make sure the aircraft is selected
          setStack({
            ...stack,
            movableObstacles: stack.movableObstacles.map((mo) => ({
              ...mo,
              selected: movableObstacle.id === mo.id ? true : false,
            })),
          });
        }}
        onClickNotesButton={() => {
          // No notes functionality for obstacles
        }}
        feetToPixels={feetToPixels}
      />
      <Popover
        id={`popover-right-click-${movableObstacle.entity_id}`}
        open={rightClick?.entity_id === movableObstacle.entity_id}
        onClose={() => setRightClick(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          rightClick !== null
            ? {
                top: rightClick.mouseY as number,
                left: rightClick.mouseX as number,
              }
            : undefined
        }
      >
        <MovableObstacleContextMenu
          onDelete={async () => {
            setStack({
              ...stack,
              movableObstacles: stack.movableObstacles.filter(
                (e) => e.entity_id !== movableObstacle.entity_id
              ),
            });
            setRightClick(null);
          }}
          menuProps={{
            open: true,
          }}
        />
      </Popover>
    </>
  );
};
